import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Label, Spinner } from 'reactstrap';
import { api } from '../../api/client.js';
import { WeekSelector } from '../../components/WeekSelector.jsx';
import { GameCard } from '../../components/GameCard.jsx';
import { useCurrentWeek } from '../../hooks/useCurrentWeek.js';

/** Lets an admin set or correct another player's picks. Deadlines do not apply here. */
export function AdminPicks() {
  const current = useCurrentWeek();
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [season, setSeason] = useState(null);
  const [week, setWeek] = useState(null);

  const [schedule, setSchedule] = useState(null);
  const [selections, setSelections] = useState({});
  const [tiebreaker, setTiebreaker] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.admin.users().then(setUsers).catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    if (current.season && season === null) {
      setSeason(current.season);
      setWeek(current.week);
    }
  }, [current, season]);

  const load = useCallback(async () => {
    if (!season || !week || !userId) {
      setSchedule(null);
      return;
    }

    setLoading(true);
    setError(null);
    setStatus(null);

    try {
      const [weekSchedule, theirPicks] = await Promise.all([
        api.week(season, week),
        api.userPicks(season, week, userId),
      ]);

      setSchedule(weekSchedule);
      setSelections(Object.fromEntries(theirPicks.picks.map((p) => [p.gameId, p.selectedTeamId])));
      setTiebreaker(theirPicks.tiebreakerTotalPoints ?? '');
    } catch (err) {
      setError(err.message);
      setSchedule(null);
    } finally {
      setLoading(false);
    }
  }, [season, week, userId]);

  useEffect(() => { load(); }, [load]);

  const tiebreakerGame = useMemo(
    () => schedule?.games.find((g) => g.isTiebreakerGame) ?? null,
    [schedule]
  );

  async function save() {
    setSaving(true);
    setError(null);
    setStatus(null);

    try {
      const result = await api.admin.setUserPicks(userId, {
        seasonYear: season,
        weekNumber: week,
        picks: Object.entries(selections).map(([gameId, selectedTeamId]) => ({
          gameId: Number(gameId),
          selectedTeamId,
        })),
        tiebreakerTotalPoints: tiebreaker === '' ? null : Number(tiebreaker),
      });

      setStatus(result);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h1 className="h3 mb-1">Edit player picks</h1>
      <p className="text-muted small">
        Admin edits bypass the pick deadlines and are recorded against your account.
        Saving re-scores the week.
      </p>

      <div className="mb-3" style={{ maxWidth: 320 }}>
        <Label for="pick-user" className="mb-1 small text-muted">Player</Label>
        <Input id="pick-user" type="select" value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">Select a player&hellip;</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
        </Input>
      </div>

      <WeekSelector season={season} week={week} disabled={loading}
        onChange={(s, w) => { setSeason(s); setWeek(w); }} />

      {error && <Alert fade={false} color="danger">{error}</Alert>}

      {status && (
        <Alert fade={false} color={status.rejected.length > 0 ? 'warning' : 'success'}>
          Saved {status.accepted} pick{status.accepted === 1 ? '' : 's'}.
          {status.rejected.length > 0 && (
            <ul className="mb-0 mt-2">{status.rejected.map((r) => <li key={r}>{r}</li>)}</ul>
          )}
        </Alert>
      )}

      {!userId && <Alert fade={false} color="secondary">Choose a player to edit their picks.</Alert>}
      {loading && <div className="text-center py-4"><Spinner /></div>}

      {!loading && schedule && userId && (
        <>
          <div className="game-grid">
            {schedule.games.map((game) => (
              <GameCard key={game.id} game={game} selectedTeamId={selections[game.id]}
                locked={false}
                onSelect={(gameId, teamId) => setSelections({ ...selections, [gameId]: teamId })} />
            ))}
          </div>

          <div className="mt-4 p-3 border rounded bg-body-tertiary">
            <Label for="admin-tiebreaker" className="fw-semibold">
              Tiebreaker &mdash; {tiebreakerGame
                ? `${tiebreakerGame.away.abbreviation} @ ${tiebreakerGame.home.abbreviation}`
                : 'last game of the week'}
            </Label>
            <Input id="admin-tiebreaker" type="number" min="0" style={{ maxWidth: 160 }}
              value={tiebreaker} onChange={(e) => setTiebreaker(e.target.value)} />
          </div>

          <Button color="primary" className="mt-3" onClick={save} disabled={saving}>
            {saving ? <Spinner size="sm" /> : 'Save picks'}
          </Button>
        </>
      )}
    </div>
  );
}
